/* eslint-disable no-useless-escape */

import {
  TransactionProps,
  MaskOptions,
  TimeUnit,
  // CryptoData,
  Coin,
  CryptoDetails,
} from "../types/interface";
import { useEffect, useCallback, useMemo, useRef } from "react";
import amplitude from "amplitude-js";

export const formatCurrency = (value: number): string => {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
  }).format(value);
};

export const calculateTransactionDetails = (
  sendingMoneyData: TransactionProps
) => {
  if (!sendingMoneyData?.amount) {
    return {
      amount: 0,
      feeAmount: 0,
      totalAmount: 0,
      feeRate: 0,
    };
  }

  // For e-transfer and bank transfer, no additional fees
  const numAmount = sendingMoneyData.amount;
  const feeRate = 0;
  const feeAmount = 0;
  const totalAmount = numAmount;

  return {
    amount: numAmount,
    feeAmount,
    totalAmount,
    feeRate,
  };
};

export const universalMask = (
  input: string,
  options: MaskOptions = {}
): string => {
  if (!input) return "";

  const {
    maskChar = "*",
    maskEmail = true,
    maskPhone = true,
    maskCard = true,
    preserveSpecial = true,
  } = options;

  // Helper function to ensure 4-character output for non-email
  const formatTo4Chars = (str: string): string => {
    if (str.length > 4) {
      return str.slice(0, 1) + maskChar.repeat(2) + str.slice(-1);
    }
    if (str.length < 4) {
      return str + maskChar.repeat(4 - str.length);
    }
    return str;
  };

  // Helper function to mask a simple string
  const basicMask = (str: string): string => {
    const start = str.slice(0, 1);
    const end = str.slice(-1);
    return formatTo4Chars(start + maskChar.repeat(2) + end);
  };

  // Helper function to truncate string to max length
  const truncateString = (str: string, maxLength: number): string => {
    return str.length > maxLength ? str.slice(0, maxLength) : str;
  };

  // Check if it's an email address
  if (maskEmail && input.includes("@")) {
    const [username] = input.split("@");
    const truncatedUsername = truncateString(username, 12);
    const maskedUsername =
      truncatedUsername.slice(0, 1) +
      maskChar.repeat(Math.min(2, truncatedUsername.length - 2)) +
      truncatedUsername.slice(-1);
    return `${maskedUsername}@${maskChar}.com`;
  }

  // Check if it's a credit card number
  if (maskCard && input.replace(/[^0-9]/g, "").length >= 13) {
    const digits = input.replace(/[^0-9]/g, "");
    const truncatedCard = truncateString(digits, 7);
    return formatTo4Chars(
      truncatedCard.slice(0, 1) + maskChar.repeat(2) + truncatedCard.slice(-1)
    );
  }

  // Check if it's a phone number
  if (maskPhone && /^[\d\s\-\(\)\+\.]+$/.test(input)) {
    const digits = input.replace(/[^0-9]/g, "");
    if (digits.length >= 7) {
      const truncatedPhone = truncateString(digits, 7);
      return formatTo4Chars(
        truncatedPhone.slice(0, 1) +
          maskChar.repeat(2) +
          truncatedPhone.slice(-1)
      );
    }
  }

  // Default: treat as regular string
  const truncatedInput = truncateString(input, 4);
  return preserveSpecial
    ? formatTo4Chars(truncatedInput.replace(/[a-zA-Z0-9]/g, maskChar))
    : formatTo4Chars(basicMask(truncatedInput));
};

export const getDetailedTimeAgo = (date: string | Date): string => {
  const timestamp = new Date(date).getTime();
  const now = Date.now();
  const diff = Math.floor((now - timestamp) / 1000);

  if (diff < 0) return "in the future";
  if (diff < 30) return "just now";

  const years = Math.floor(diff / 31536000);
  const months = Math.floor((diff % 31536000) / 2592000);
  const days = Math.floor((diff % 2592000) / 86400);
  const hours = Math.floor((diff % 86400) / 3600);
  const minutes = Math.floor((diff % 3600) / 60);

  const parts = [];

  if (years > 0) parts.push(`${years}y`);
  if (months > 0) parts.push(`${months}m`);
  if (days > 0) parts.push(`${days}d`);
  if (hours > 0) parts.push(`${hours}h`);
  if (minutes > 0) parts.push(`${minutes}min`);

  return parts.length > 0 ? parts.slice(0, 2).join(" ") : "just now";
};

export const getTimeAgo = (date: string | Date): string => {
  const timeUnits: TimeUnit[] = [
    { max: 60, divisor: 1, unit: "second", pastTense: "seconds ago" },
    { max: 3600, divisor: 60, unit: "minute", pastTense: "minutes ago" },
    { max: 86400, divisor: 3600, unit: "hour", pastTense: "hours ago" },
    { max: 2592000, divisor: 86400, unit: "day", pastTense: "days ago" },
    { max: 31536000, divisor: 2592000, unit: "month", pastTense: "months ago" },
    { max: Infinity, divisor: 31536000, unit: "year", pastTense: "years ago" },
  ];

  const timestamp = new Date(date).getTime();
  const now = Date.now();
  const secondsAgo = Math.floor((now - timestamp) / 1000);

  // Handle future dates
  if (secondsAgo < 0) {
    return "in the future";
  }

  // Just now - less than 30 seconds ago
  if (secondsAgo < 30) {
    return "just now";
  }

  // Find the appropriate time unit
  const timeUnit =
    timeUnits.find((unit) => secondsAgo < unit.max) ||
    timeUnits[timeUnits.length - 1];
  const value = Math.floor(secondsAgo / timeUnit.divisor);

  // Handle singular cases
  if (value === 1) {
    return `1 ${timeUnit.unit} ago`;
  }

  return `${value} ${timeUnit.pastTense}`;
};

export function parseWebSocketMessage(rawMessage: string): any[] {
  try {
    const jsonStr = rawMessage.substring(
      rawMessage.indexOf("{"),
      rawMessage.lastIndexOf("]")
    );
    const parsed = JSON.parse(jsonStr);
    return parsed.data.transactions;
  } catch (error) {
    throw new Error(`Failed to parse WebSocket message: ${error}`);
  }
}

export const getTransactionInfo = (transaction: any) => {
  switch (transaction.transactionType) {
    case "BANK_DEPOSIT":
      return {
        initials: "BD",
        receiver: universalMask(transaction?.paymentDetails?.accountNumber),
        created_at: getTimeAgo(transaction.updatedAt),
        sender: universalMask(transaction.userId.username),
      };

    case "ETRANSFER":
      return {
        initials: "ET",
        receiver: universalMask(transaction?.paymentDetails?.email),
        created_at: getTimeAgo(transaction.updatedAt),
        sender: universalMask(transaction.userId.username),
      };

    default:
      return {
        initials: "null",
      };
  }
};

interface EnhancedCryptoDetails extends CryptoDetails {
  address: string;
}

type CryptoData = Record<string, CryptoDetails>;
type EnhancedCryptoData = Record<string, EnhancedCryptoDetails>;

const getAddressForCrypto = (key: string): string => {
  // This is a placeholder function - replace with actual address generation/fetching logic
  const addressMap: Record<string, string> = {
    btc: "38p8hPVkaPFBQZ4fTD4Lviizp1NLCZNEKJ",
    doge: "D6HjskYXuxAn9GcpXJFESXzzPt5jqWf5eh",
    eth: "0x0B01430Cb0C26AE863EA86FB7ff7eb41f2F25A10",
    sol_sol: "BxGsHQmk7zBdpN9gAKm3upWJzRxFiR8LMqugoJ7ytLAu",
  };

  return addressMap[key] || "";
};

export const filterCryptoData = (
  coins: CryptoDetails[] | Record<string, CryptoDetails>,
  coinsToInclude: string[]
): CryptoDetails[] => {
  const baseObject =
    !Array.isArray(coins) && typeof coins === "object"
      ? coins
      : Array.isArray(coins)
      ? coins.reduce<CryptoData>((acc, coin) => {
          acc[coin.ticker as keyof CryptoData] = coin;
          return acc;
        }, {})
      : {};

  const filtered = Object.fromEntries(
    Object.entries(baseObject)
      .filter(([key]) => coinsToInclude.includes(key))
      .map(([key, value]) => [
        key,
        {
          ...value,
          address: getAddressForCrypto(key),
        },
      ])
  );

  return Object.values(filtered);
};

export const useMoneyTransfer = (
  params: any,
  sendingType: any,
  dispatch: any,
  sendingMoneyData: any
): any => {
  const dispatchedRef = useRef(false);

  const paymentData = useMemo(() => {
    if (!sendingType || !params) return null;
    const getPaymentDetails = () => {
      switch (sendingType) {
        case "bank": {
          const bankInfo = params.bankInfo ? JSON.parse(params.bankInfo) : {};
          return {
            amount: params.amount,
            transactionType: "BANK_DEPOSIT",
            paymentDetails: {
              transitNumber: bankInfo.transit || "John Doe",
              institutionNumber: bankInfo.institution || "John Doe",
              accountNumber: bankInfo.account || "John Doe",
            },
          };
        }

        case "etransfer": {
          return {
            amount: params.amount,
            transactionType: "ETRANSFER",
            paymentDetails: {
              fullName: "John Doe",
              email: params.email,
            },
          };
        }

        default:
          return null;
      }
    };

    return getPaymentDetails();
  }, [sendingType, params]);

  useEffect(() => {
    if (paymentData && !dispatchedRef.current) {
      dispatchedRef.current = true;
      dispatch(sendingMoneyData(paymentData));
    }
  }, [dispatch, paymentData, sendingMoneyData]);

  return paymentData;
};

export function getIcon(symbol: string) {
  switch (symbol) {
    case "btc":
      return "https://cryptologos.cc/logos/bitcoin-btc-logo.png";
    case "eth":
      return "https://cryptologos.cc/logos/ethereum-eth-logo.png";
    case "doge":
      return "https://cryptologos.cc/logos/dogecoin-doge-logo.png";
    case "sol":
      return "https://cryptologos.cc/logos/solana-sol-logo.png";
    default:
      return "https://cryptologos.cc/logos/bitcoin-btc-logo.png";
  }
}

// Initialize Amplitude
export const initAmplitude = (apiKey: string) => {
  amplitude.getInstance().init(apiKey);
};

// Type for button click events
export interface ButtonClickEvent {
  buttonName: string;
  location?: string;
  additionalData?: Record<string, any>;
}

// Track button clicks
export const trackButtonClick = (data: ButtonClickEvent) => {
  amplitude.getInstance().logEvent("Button Clicked", {
    ...data,
    timestamp: new Date().toISOString(),
  });
};

export const useAmplitudeTracking = () => {
  const logButtonClick = useCallback((data: ButtonClickEvent) => {
    trackButtonClick(data);
  }, []);

  return { logButtonClick };
};

export const handleGetStarted = async (
  signOut: any,
  dispatch: any,
  logout: any
) => {
  try {
    await signOut();
    // Clear any local storage if needed
    localStorage.clear();
    sessionStorage.clear();
    dispatch(logout());
  } catch (error) {
    console.error("Sign out error:", error);
  }
};

export const formatCurrencyV2 = (value: string): string => {
  const match = value.match(/^([\d.]+)\s*([A-Z]{3})?$/);

  if (match) {
    const amount = parseFloat(match[1]);
    const currency = match[2] || "CAD";
    const formattedAmount = amount.toLocaleString("en-CA", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });

    return `${formattedAmount} ${currency}`;
  }
  return value;
};
